import classNames from 'classnames';
import { ForwardedRef } from 'react';

import { DrawingTabEnum } from '@common/enums/drawing-tab.enum';
import { PredefinedShape } from '@common/models';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import { usePredefinedShapes } from '@core/hooks/usePredefinedShapes';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useFabricCanvasMethods } from '@fabricCanvas/fabricCanvas';
import { useAppDispatch } from '@redux/hooks';
import { updateSelectedDrawingTab } from '@redux/slices/shapeSlice';

import { SelectedMaterialColor } from '../selected-material-color/selectedMaterialColor';

import './predefinedShapeMenu.scss';

export interface PredefinedShapeItemProps {
    shape: PredefinedShape;
    isSelected: boolean;
    disable: boolean;
    onClickPredefinedShape?: (predefinedShape: PredefinedShape) => unknown;
}

const PredefinedShapeItem = ({ shape, isSelected, disable, onClickPredefinedShape }: PredefinedShapeItemProps) => {
    return (
        <div
            className={classNames('shape-container', {
                selected: !disable && isSelected,
                disabled: disable,
                enabled: !disable,
            })}
            onClick={() => onClickPredefinedShape?.(shape)}
        >
            <img
                className={classNames('shape-image', {
                    selected: !disable && isSelected,
                    disabled: disable,
                    enabled: !disable,
                })}
                alt={shape.title}
                src={shape.image}
            />
            <p
                className={classNames('shape-title', {
                    selected: !disable && isSelected,
                    disabled: disable,
                    enabled: !disable,
                })}
            >
                {shape.title}
            </p>
        </div>
    );
};

export interface PredefinedShapeMenuProps {
    shapeContainer: ForwardedRef<HTMLDivElement>;
    disable: boolean;
}

export const PredefinedShapeMenu = ({ shapeContainer, disable }: PredefinedShapeMenuProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const { predefinedShapes } = usePredefinedShapes();
    // const selectedPredefinedShape = useAppSelector(selectSelectedPredefinedShape);
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { addPredefinedShape } = useFabricCanvasMethods();

    const { trackAddShape } = useAnalyticsEvents();

    const selectShape = (shape: PredefinedShape) => {
        if (!disable) {
            dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));
            addPredefinedShape(shape);
            trackAddShape({ shape });
        }
    };

    return (
        <div className="add-layout-area">
            <SelectedMaterialColor selectedColor={selectedColor} selectedMaterial={selectedMaterial} />
            <div ref={shapeContainer} className="shape-menu">
                {predefinedShapes.map((shape) => (
                    <PredefinedShapeItem
                        onClickPredefinedShape={selectShape}
                        key={shape.id}
                        shape={shape}
                        isSelected={false}
                        disable={disable}
                    />
                ))}
            </div>
        </div>
    );
};
